import dayjs from 'dayjs'
import type { ComponentProps } from 'react'

import type { EHRAppointmentFormType } from 'features/Home/schemas/infoNewPatientSchema'
import type { IPatientRoom } from 'features/Home/interfaces/IInfoPatient'

import { FooterBar, footerSubtitleOf, footerTitleOf } from './FooterBar'
import type { SeriesTab } from './Series/useSeriesTab'
import type { AppointmentPeople } from './useAppointmentPeople'

type FooterProps = ComponentProps<typeof FooterBar>

type Args = {
  values: EHRAppointmentFormType
  selectedDate: string
  rooms: IPatientRoom[]
  currentDay: string
  visitStart: number
  people: Pick<AppointmentPeople, 'label' | 'multiple' | 'people'>
  error?: string
  canSave: boolean
  isSaving: boolean
  isEditing: boolean
  search: { hasSearched: boolean; clear: () => void }
  picked: { clear: () => void }
  onSave: () => void
  onDelete: () => void
  onCancel: () => void
  // The Series tab swaps in its own summary, Save and no Delete.
  tab: Pick<SeriesTab, 'isSeries' | 'footer' | 'canSave'>
  onSaveSeries: () => void
}

// What is booked, when and where; on the Series tab, the Series summary.
export const useFooterProps = ({
  values,
  selectedDate,
  rooms,
  currentDay,
  visitStart,
  people,
  error,
  canSave,
  isSaving,
  isEditing,
  search,
  picked,
  onSave,
  onDelete,
  onCancel,
  tab,
  onSaveSeries,
}: Args): FooterProps => {
  if (tab.isSeries && tab.footer) {
    const { kicker, title, subtitle, tone, saveLabel } = tab.footer
    return {
      kicker,
      title,
      subtitle,
      tone,
      saveLabel,
      isSaving,
      canSave: tab.canSave,
      canDelete: false,
      hasSearched: false,
      onClearSearch: () => undefined,
      onSave: onSaveSeries,
      onDelete,
      onCancel,
    }
  }
  const room = rooms.find((option) => String(option.value) === String(values.room))
  return {
    title: footerTitleOf(visitStart, values.duration ?? 0, people.label),
    subtitle: footerSubtitleOf({
      day: dayjs(values.date ?? selectedDate),
      roomName: room?.short_name ?? room?.label,
      isLoadedDay: currentDay === dayjs(selectedDate).format('YYYY-MM-DD'),
    }),
    error,
    isSaving,
    // A joint visit needs at least one person.
    canSave: canSave && (!people.multiple || people.people.length > 0),
    canDelete: isEditing,
    hasSearched: search.hasSearched,
    onClearSearch: () => {
      search.clear()
      picked.clear()
    },
    onSave,
    onDelete,
    onCancel,
  }
}
